import { axios } from '../request'
import { hasReferenceYear } from '../lib/paymentTypes'

const url = 'payment_advice'

// #418 — same defect as #415, in a store whose state is a plain object: a 2xx
// whose body is not the `{"data": …}` envelope leaves `res.data.data`
// undefined, and committing that took `data` out of the type it was born with.
// It is not a hypothetical body — axios hands `''` over for an empty response
// and the raw string for the HTML a proxy serves, because its default transform
// keeps the text when `JSON.parse` throws.
//
// Here the consumer had no guard at all. `Payments/Single.vue` opens the advice
// block on `paymentAdviceStatus === 200` and then reads `.isHonorario` /
// `.pays` straight off this state, so a bodyless 200 killed the render of the
// whole payment form, not just the advice.
const isAdvice = (data) => data !== null && typeof data === 'object' && !Array.isArray(data)

export default (_app) => {
  return {
    namespaced: true,
    state: {
      status: null,
      data: {},
    },
    mutations: {
      setData(state, data) {
        state.data = data
      },
      loading(state) {
        Object.assign(state, { loaded: false, loading: true })
      },
      loaded(state) {
        Object.assign(state, { loaded: true, loading: false })
      },
      notLoaded(state) {
        Object.assign(state, { loaded: false, loading: false })
      },
      setLastStatus(state, s) {
        state.status = s
      },
    },
    actions: {
      read(context, data) {
        return new Promise((resolve, reject) => {
          context.commit('loading')
          if (typeof data.memberId === 'undefined' || typeof data.type === 'undefined') {
            clearAdvice(context)
            reject(new Error('Not Enough Data'))
            return
          }
          if (hasReferenceYear(data.type) && typeof data.year === 'undefined') {
            clearAdvice(context)
            reject(new Error('Not Enough Data'))
            return
          }
          let params = `?memberID=${data.memberId}&type=${data.type}`
          if (hasReferenceYear(data.type)) {
            params += `&year=${data.year}`
          }
          // Cooperative jóia / títulos de capital debts are scoped per section
          // (member_state). The backend advice query filters on member_state_id,
          // so it must be forwarded — otherwise the open debt is never matched
          // and the form misleadingly shows "não deverá pagar".
          if (typeof data.memberStateId !== 'undefined' && data.memberStateId !== null) {
            params += `&memberStateId=${data.memberStateId}`
          }
          axios
            .get(`${url}${params}`)
            .then((res) => {
              if (!isAdvice(res?.data?.data)) {
                // The case that took the payment form down, and the one that
                // used to leave no trace at all: the request succeeded, so
                // there was no error to swallow — the payload simply was not
                // there.
                console.error(
                  `paymentAdvice: ${url} answered ${res?.status} without a usable envelope`,
                  res?.data
                )
                clearAdvice(context)
                reject(new Error('Could not read item'))
                return
              }
              context.commit('setData', res.data.data)
              context.commit('loaded')
              context.commit('setLastStatus', res.status)
              resolve()
            })
            .catch((err) => {
              // A 404 is how this endpoint reports "no advice for these
              // parameters" — an unknown member, a jóia with no member_state
              // date, missing query parameters (paymentAdvice/controller.go).
              // It is a routine answer on a half-filled form, not a fault, so
              // it is not logged.
              if (err?.response?.status !== 404) {
                console.error(`paymentAdvice: ${url} failed`, err)
              }
              clearAdvice(context)
              if (err.response) {
                if (err.response.status === 404) {
                  context.commit('loaded')
                }
              } else {
                context.commit('notLoaded')
              }
              reject(new Error('Could not read item'))
            })
        })
      },
    },
  }
}

// clearAdvice returns the store to the values it was born with, on every path
// that ends without advice.
//
// `data` was being reset to `{ data: {} }` — one level too deep, so the failure
// paths left `{data:{}}` where the state started as `{}` and where every
// consumer reads advice fields off the top level.
//
// `status` matters as much: `Payments/Single.vue` uses `status === 200` as its
// proxy for "we have advice", so a stale 200 left behind by an earlier
// successful read would keep claiming advice this store no longer holds. Its
// birth value, null, is the one that says "nothing to show". This is why the
// non-envelope branch does not commit `res.status`: the server did answer 200,
// but a 200 carrying no advice must not be able to open that block onto an
// empty object and announce "não deverá pagar" over a real debt.
function clearAdvice(context) {
  context.commit('setData', {})
  context.commit('setLastStatus', null)
}
